import { useDataProvider, useGetIdentity } from "ra-core";
import { useCallback, useMemo } from "react";

import type { ImportRow } from "../dataImport/types";
import type { Contact, LeadSource, Sale, Tag } from "../types";
import { createEachRow } from "../dataImport/createEachRow";
import { fetchRecordsWithCache } from "../dataImport/fetchRecordsWithCache";
import { useCompanyResolver } from "../dataImport/useCompanyResolver";
import { leadImportFields } from "./leadImport";

export type ContactImportSchema = {
  first_name: string;
  last_name: string;
  gender: string;
  title: string;
  company: string;
  email_work: string;
  email_home: string;
  email_other: string;
  phone_work: string;
  phone_home: string;
  phone_other: string;
  background: string;
  avatar: string;
  first_seen: string;
  last_seen: string;
  has_newsletter: string;
  status: string;
  tags: string;
  linkedin_url: string;
};

export function useContactImport() {
  const today = new Date().toISOString();
  const user = useGetIdentity();
  const dataProvider = useDataProvider();

  const getCompanies = useCompanyResolver();

  // Tags cache to avoid creating the same tag multiple times and costly roundtrips
  // Cache is dependent of dataProvider, so it's safe to use it as a dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tagsCache = useMemo(() => new Map<string, Tag>(), [dataProvider]);
  const getTags = useCallback(
    async (names: string[]) =>
      fetchRecordsWithCache<Tag>(
        "tags",
        tagsCache,
        names,
        (name) => ({
          name,
          color: "#f9f9f9",
        }),
        dataProvider,
      ),
    [tagsCache, dataProvider],
  );

  const processBatch = useCallback(
    async (batch: ImportRow[]) => {
      const [companies, tags, sources, sales, existing] = await Promise.all([
        getCompanies(
          batch
            .map((contact) =>
              typeof contact.company === "string" ? contact.company.trim() : "",
            )
            .filter((name) => name),
        ),
        getTags(batch.flatMap((row) => parseTags(String(row.tags ?? "")))),
        dataProvider.getList<LeadSource>("lead_sources", {
          pagination: { page: 1, perPage: 100 },
          sort: { field: "name", order: "ASC" },
        }),
        dataProvider.getList<Sale>("sales", {
          pagination: { page: 1, perPage: 100 },
          sort: { field: "last_name", order: "ASC" },
        }),
        dataProvider.getList<Contact>("contacts", {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: "id", order: "ASC" },
        }),
      ]);
      const sourceIds = new Map(
        sources.data.map((source) => [source.name.toLowerCase(), source.id]),
      );
      const ensureSource = async (name: string | undefined) => {
        if (!name) return undefined;
        const found = sourceIds.get(name.toLowerCase());
        if (found != null) return found;
        const { data } = await dataProvider.create<LeadSource>("lead_sources", {
          data: { name },
        });
        sourceIds.set(name.toLowerCase(), data.id);
        return data.id;
      };
      const byEmail = new Map<string, Contact>();
      for (const contact of existing.data) {
        for (const item of contact.email_jsonb ?? []) {
          if (item.email) byEmail.set(item.email.trim().toLowerCase(), contact);
        }
      }

      return createEachRow(
        batch.map((row) => {
          const fields = leadImportFields(row);
          const email_jsonb = [
            { email: fields.email, type: "Work" as const },
            { email: textCell(row.email_home), type: "Home" as const },
            { email: textCell(row.email_other), type: "Other" as const },
          ].filter((item) => item.email);
          const phone_jsonb = [
            { number: fields.phone, type: "Work" as const },
            { number: textCell(row.phone_home), type: "Home" as const },
            { number: textCell(row.phone_other), type: "Other" as const },
          ].filter((item) => item.number);
          const companyName = textCell(row.company);
          const company = companyName ? companies.get(companyName) : undefined;
          const tagList = parseTags(String(row.tags ?? ""))
            .map((name) => tags.get(name))
            .filter((tag): tag is Tag => !!tag);
          const assignee = sales.data.find(
            (sale) =>
              `${sale.first_name} ${sale.last_name}`.toLowerCase() ===
              fields.assignee?.toLowerCase(),
          );
          const data = {
            first_name: fields.first_name,
            last_name: fields.last_name,
            gender: textCell(row.gender),
            title: textCell(row.title),
            email_jsonb,
            phone_jsonb,
            background: fields.background,
            first_seen: fields.first_seen ?? today,
            last_seen: textCell(row.last_seen)
              ? new Date(String(row.last_seen)).toISOString()
              : today,
            has_newsletter: row.has_newsletter,
            status: textCell(row.status),
            company_id: company?.id,
            tags: tagList.map((tag) => tag.id),
            sales_id: assignee?.id ?? user?.identity?.id,
            linkedin_url: textCell(row.linkedin_url),
            stage: fields.stage,
          };
          const email = fields.email?.trim().toLowerCase();
          const current = email ? byEmail.get(email) : undefined;
          return ensureSource(fields.sourceName).then(async (sourceId) => {
            const payload = { ...data, source_id: sourceId };
            if (!current || !email) {
              const created = await dataProvider.create<Contact>("contacts", {
                data: payload,
              });
              if (email) byEmail.set(email, created.data);
              return created;
            }
            return dataProvider.update("contacts", {
              id: current.id,
              data: payload,
              previousData: current,
            });
          });
        }),
      );
    },
    [dataProvider, getCompanies, getTags, user?.identity?.id, today],
  );

  return processBatch;
}

const textCell = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const parseTags = (tags: string) =>
  tags
    ?.split(",")
    ?.map((tag: string) => tag.trim())
    ?.filter((tag: string) => tag) ?? [];
